import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const calculateExperience = (startDate) => {
  if (!startDate) return '';
  
  const start = new Date(startDate);
  const today = new Date();
  
  if (start > today) return '0 years 0 months';
  
  let years = today.getFullYear() - start.getFullYear();
  let months = today.getMonth() - start.getMonth();
  
  if (months < 0) {
    years--;
    months += 12;
  }
  
  return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
};

const EmployeeFormFields = ({ employeeForm, setEmployeeForm, managers, editingEmployee }) => {
  const [rmLookupLoading, setRmLookupLoading] = useState(false);
  
  // Lookup RM name when email is entered
  const handleRmEmailChange = async (email) => {
    setEmployeeForm({ ...employeeForm, rm_email: email });
    
    // Only lookup if valid email format
    if (email && email.includes('@') && email.includes('.')) {
      setRmLookupLoading(true);
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${API}/users/lookup-by-email?email=${encodeURIComponent(email)}`, config);
        
        if (response.data && response.data.name) {
          setEmployeeForm(prev => ({ ...prev, reporting_manager: response.data.name }));
          toast.success(`Found: ${response.data.name}`);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // If not found, allow manual entry but clear the field
          setEmployeeForm(prev => ({ ...prev, reporting_manager: '' }));
          // Don't show error, just allow manual entry
        }
      } finally {
        setRmLookupLoading(false);
      }
    }
  };
  
  // Auto-calculate Total Work Experience
  useEffect(() => {
    if (employeeForm.actual_date_of_joining) {
      const totalExp = calculateExperience(employeeForm.actual_date_of_joining);
      setEmployeeForm(prev => ({ ...prev, total_work_experience: totalExp }));
    }
  }, [employeeForm.actual_date_of_joining]);
  
  // Auto-calculate Work Experience as Employee
  useEffect(() => {
    let expAsEmployee = '';
    
    if (employeeForm.converted_employee && employeeForm.conversion_date) {
      // If converted, calculate from conversion date
      expAsEmployee = calculateExperience(employeeForm.conversion_date);
    } else if (employeeForm.actual_date_of_joining) {
      // If not converted, calculate from actual date of joining
      expAsEmployee = calculateExperience(employeeForm.actual_date_of_joining);
    }
    
    if (expAsEmployee) {
      setEmployeeForm(prev => ({ ...prev, work_experience_as_employee: expAsEmployee }));
    }
  }, [employeeForm.converted_employee, employeeForm.conversion_date, employeeForm.actual_date_of_joining]);
  return (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="basic">Basic Info</TabsTrigger>
        <TabsTrigger value="personal">Personal Details</TabsTrigger>
        <TabsTrigger value="employment">Employment Details</TabsTrigger>
        <TabsTrigger value="salary">Salary Structure</TabsTrigger>
      </TabsList>

      {/* Basic Information Tab */}
      <TabsContent value="basic" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Profile Photo Upload */}
          <div className="col-span-2">
            <Label>Profile Photo (Passport Size)</Label>
            <div className="flex items-center gap-4">
              {employeeForm.profile_photo && (
                <img 
                  src={employeeForm.profile_photo} 
                  alt="Profile" 
                  className="w-24 h-24 rounded-full object-cover border-2 border-blue-500"
                />
              )}
              <div className="flex-1">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Convert to base64
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setEmployeeForm({ ...employeeForm, profile_photo: reader.result });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">Upload passport size photo (JPG, PNG)</p>
              </div>
            </div>
          </div>
          <div>
            <Label>Employee ID *</Label>
            <Input
              value={employeeForm.employee_id}
              onChange={(e) => setEmployeeForm({ ...employeeForm, employee_id: e.target.value })}
              required
              disabled={!!editingEmployee}
              data-testid="employee-id-input"
            />
          </div>
          <div>
            <Label>Full Name *</Label>
            <Input
              value={employeeForm.name}
              onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
              required
              data-testid="employee-name-input"
            />
          </div>
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={employeeForm.email}
              onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
              required
              data-testid="employee-email-input"
            />
          </div>
          <div>
            <Label>Password {editingEmployee && '(leave blank to keep current)'}</Label>
            <Input
              type="password"
              value={employeeForm.password}
              onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
              required={!editingEmployee}
              data-testid="employee-password-input"
            />
          </div>
          <div>
            <Label>Role *</Label>
            <Select value={employeeForm.role} onValueChange={(value) => setEmployeeForm({ ...employeeForm, role: value })}>
              <SelectTrigger data-testid="employee-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date of Joining *</Label>
            <Input
              type="date"
              value={employeeForm.date_of_joining}
              onChange={(e) => setEmployeeForm({ ...employeeForm, date_of_joining: e.target.value })}
              required
              data-testid="employee-doj-input"
            />
          </div>
          <div>
            <Label>Department</Label>
            <Input
              value={employeeForm.department}
              onChange={(e) => setEmployeeForm({ ...employeeForm, department: e.target.value })}
              data-testid="employee-department-input"
            />
          </div>
          <div>
            <Label>Designation</Label>
            <Input
              value={employeeForm.designation}
              onChange={(e) => setEmployeeForm({ ...employeeForm, designation: e.target.value })}
              data-testid="employee-designation-input"
            />
          </div>
        </div>
      </TabsContent>

      {/* Personal Details Tab */}
      <TabsContent value="personal" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Gender</Label>
            <Select value={employeeForm.gender} onValueChange={(value) => setEmployeeForm({ ...employeeForm, gender: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date of Birth</Label>
            <Input
              type="date"
              value={employeeForm.dob}
              onChange={(e) => setEmployeeForm({ ...employeeForm, dob: e.target.value })}
            />
          </div>
          <div>
            <Label>Marital Status</Label>
            <Select value={employeeForm.marital_status} onValueChange={(value) => setEmployeeForm({ ...employeeForm, marital_status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Single">Single</SelectItem>
                <SelectItem value="Married">Married</SelectItem>
                <SelectItem value="Divorced">Divorced</SelectItem>
                <SelectItem value="Widowed">Widowed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nationality</Label>
            <Input
              value={employeeForm.nationality}
              onChange={(e) => setEmployeeForm({ ...employeeForm, nationality: e.target.value })}
              placeholder="e.g., Indian"
            />
          </div>
          <div className="col-span-2">
            <Label>Present Address</Label>
            <Textarea
              value={employeeForm.present_address}
              onChange={(e) => setEmployeeForm({ ...employeeForm, present_address: e.target.value })}
              rows={2}
            />
          </div>
          <div className="col-span-2">
            <Label>Permanent Address</Label>
            <Textarea
              value={employeeForm.permanent_address}
              onChange={(e) => setEmployeeForm({ ...employeeForm, permanent_address: e.target.value })}
              rows={2}
            />
          </div>
          <div>
            <Label>Contact Number</Label>
            <Input
              type="tel"
              value={employeeForm.contact_number}
              onChange={(e) => setEmployeeForm({ ...employeeForm, contact_number: e.target.value })}
              placeholder="+91 9876543210"
            />
          </div>
          <div>
            <Label>Alternate Number</Label>
            <Input
              type="tel"
              value={employeeForm.alternate_number}
              onChange={(e) => setEmployeeForm({ ...employeeForm, alternate_number: e.target.value })}
            />
          </div>
          <div>
            <Label>Alternate Email</Label>
            <Input
              type="email"
              value={employeeForm.alternate_email}
              onChange={(e) => setEmployeeForm({ ...employeeForm, alternate_email: e.target.value })}
            />
          </div>
          <div>
            <Label>Education</Label>
            <Input
              value={employeeForm.education}
              onChange={(e) => setEmployeeForm({ ...employeeForm, education: e.target.value })}
              placeholder="e.g., B.Tech, MBA"
            />
          </div>
          <div className="col-span-2">
            <Label>Previous Employment</Label>
            <Textarea
              value={employeeForm.previous_employment}
              onChange={(e) => setEmployeeForm({ ...employeeForm, previous_employment: e.target.value })}
              rows={2}
              placeholder="Previous company details"
            />
          </div>
          <div>
            <Label>Physically Disabled</Label>
            <Select value={employeeForm.physically_disabled || 'No'} onValueChange={(value) => setEmployeeForm({ ...employeeForm, physically_disabled: value })}>
              <SelectTrigger data-testid="physically-disabled-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </TabsContent>

      {/* Employment Details Tab */}
      <TabsContent value="employment" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Employee Type</Label>
            <Select value={employeeForm.employee_type} onValueChange={(value) => setEmployeeForm({ ...employeeForm, employee_type: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Full-Time">Full-Time</SelectItem>
                <SelectItem value="Part-Time">Part-Time</SelectItem>
                <SelectItem value="Contract">Contract</SelectItem>
                <SelectItem value="Intern">Intern</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {employeeForm.employee_type === 'Contract' && (
            <>
              <div>
                <Label>Contract End Date</Label>
                <Input
                  type="date"
                  value={employeeForm.contract_end_date || ''}
                  onChange={(e) => {
                    const endDate = e.target.value;
                    let period = '';
                    if (endDate && employeeForm.date_of_joining) {
                      const start = new Date(employeeForm.date_of_joining);
                      const end = new Date(endDate);
                      const diffMs = end - start;
                      if (diffMs > 0) {
                        const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const years = Math.floor(totalDays / 365);
                        const months = Math.floor((totalDays % 365) / 30);
                        const days = totalDays % 30;
                        const parts = [];
                        if (years > 0) parts.push(`${years} Year${years > 1 ? 's' : ''}`);
                        if (months > 0) parts.push(`${months} Month${months > 1 ? 's' : ''}`);
                        if (days > 0) parts.push(`${days} Day${days > 1 ? 's' : ''}`);
                        period = parts.join(', ') || '0 Days';
                      }
                    }
                    setEmployeeForm({ ...employeeForm, contract_end_date: endDate, contract_period: period });
                  }}
                  data-testid="contract-end-date"
                />
              </div>
              <div>
                <Label>Contract Period</Label>
                <Input
                  value={employeeForm.contract_period || ''}
                  readOnly
                  className="bg-gray-50"
                  placeholder="Auto-calculated from DOJ & End Date"
                  data-testid="contract-period"
                />
              </div>
            </>
          )}
          {employeeForm.employee_type === 'Intern' && (
            <>
              <div>
                <Label>Internship End Date</Label>
                <Input
                  type="date"
                  value={employeeForm.internship_end_date || ''}
                  onChange={(e) => {
                    const endDate = e.target.value;
                    let period = '';
                    if (endDate && employeeForm.date_of_joining) {
                      const start = new Date(employeeForm.date_of_joining);
                      const end = new Date(endDate);
                      const diffMs = end - start;
                      if (diffMs > 0) {
                        const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const years = Math.floor(totalDays / 365);
                        const months = Math.floor((totalDays % 365) / 30);
                        const days = totalDays % 30;
                        const parts = [];
                        if (years > 0) parts.push(`${years} Year${years > 1 ? 's' : ''}`);
                        if (months > 0) parts.push(`${months} Month${months > 1 ? 's' : ''}`);
                        if (days > 0) parts.push(`${days} Day${days > 1 ? 's' : ''}`);
                        period = parts.join(', ') || '0 Days';
                      }
                    }
                    setEmployeeForm({ ...employeeForm, internship_end_date: endDate, internship_period: period });
                  }}
                  data-testid="internship-end-date"
                />
              </div>
              <div>
                <Label>Internship Period</Label>
                <Input
                  value={employeeForm.internship_period || ''}
                  readOnly
                  className="bg-gray-50"
                  placeholder="Auto-calculated from DOJ & End Date"
                  data-testid="internship-period"
                />
              </div>
            </>
          )}
          <div>
            <Label>Level</Label>
            <Input
              value={employeeForm.level}
              onChange={(e) => setEmployeeForm({ ...employeeForm, level: e.target.value })}
              placeholder="e.g., L1, L2, Senior"
            />
          </div>
          <div>
            <Label>RM Email *</Label>
            <Input
              type="email"
              value={employeeForm.rm_email}
              onChange={(e) => handleRmEmailChange(e.target.value)}
              placeholder="manager@company.com"
              disabled={rmLookupLoading}
            />
            <p className="text-xs text-gray-500 mt-1">
              {rmLookupLoading ? 'Looking up manager...' : 'Email of reporting manager'}
            </p>
          </div>
          <div>
            <Label>Reporting Manager (Auto-filled)</Label>
            <Input
              value={employeeForm.reporting_manager}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              placeholder="Auto-filled from RM Email"
            />
            <p className="text-xs text-gray-500 mt-1">Auto-populated based on RM Email</p>
          </div>
          <div>
            <Label>HR Partner</Label>
            <Input
              value={employeeForm.hr_partner}
              onChange={(e) => setEmployeeForm({ ...employeeForm, hr_partner: e.target.value })}
              placeholder="HR Partner Name"
            />
          </div>
          <div>
            <Label>HR Email *</Label>
            <Input
              type="email"
              value={employeeForm.hr_email || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, hr_email: e.target.value })}
              placeholder="hr@company.com"
              data-testid="hr-email-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Email of HR for approval workflows (Advance Salary Requests)
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={employeeForm.converted_employee}
              onCheckedChange={(checked) => setEmployeeForm({ ...employeeForm, converted_employee: checked })}
            />
            <Label>Converted Employee</Label>
          </div>
          <div>
            <Label>Conversion Date</Label>
            <Input
              type="date"
              value={employeeForm.conversion_date}
              onChange={(e) => setEmployeeForm({ ...employeeForm, conversion_date: e.target.value })}
              disabled={!employeeForm.converted_employee}
            />
          </div>
          <div>
            <Label>Actual Date of Joining</Label>
            <Input
              type="date"
              value={employeeForm.actual_date_of_joining}
              onChange={(e) => setEmployeeForm({ ...employeeForm, actual_date_of_joining: e.target.value })}
            />
          </div>
          <div>
            <Label>Company Asset</Label>
            <Input
              value={employeeForm.company_asset}
              onChange={(e) => setEmployeeForm({ ...employeeForm, company_asset: e.target.value })}
              placeholder="e.g., Laptop, Phone"
            />
          </div>
          <div>
            <Label>Serial Number</Label>
            <Input
              value={employeeForm.serial_number}
              onChange={(e) => setEmployeeForm({ ...employeeForm, serial_number: e.target.value })}
              placeholder="Asset serial number"
            />
          </div>
          <div>
            <Label>Total Work Experience (Auto-calculated)</Label>
            <Input
              value={employeeForm.total_work_experience}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              placeholder="Auto-calculated from Actual Date of Joining"
            />
            <p className="text-xs text-gray-500 mt-1">Calculated from Actual Date of Joining</p>
          </div>
          <div>
            <Label>Work Experience as Employee (Auto-calculated)</Label>
            <Input
              value={employeeForm.work_experience_as_employee}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              placeholder="Auto-calculated based on conversion status"
            />
            <p className="text-xs text-gray-500 mt-1">
              {employeeForm.converted_employee 
                ? "Calculated from Conversion Date" 
                : "Calculated from Actual Date of Joining"}
            </p>
          </div>
          <div>
            <Label>PAN Number</Label>
            <Input
              value={employeeForm.pan_number}
              onChange={(e) => setEmployeeForm({ ...employeeForm, pan_number: e.target.value.toUpperCase() })}
              placeholder="ABCDE1234F"
              maxLength={10}
            />
          </div>
          <div>
            <Label>Aadhar Number</Label>
            <Input
              value={employeeForm.aadhar_number}
              onChange={(e) => setEmployeeForm({ ...employeeForm, aadhar_number: e.target.value })}
              placeholder="1234 5678 9012"
              maxLength={14}
            />
          </div>
          <div className="col-span-2">
            <Label>Bank Details</Label>
            <Textarea
              value={employeeForm.bank_details}
              onChange={(e) => setEmployeeForm({ ...employeeForm, bank_details: e.target.value })}
              rows={2}
              placeholder="Bank name, Account number, IFSC code"
            />
          </div>
          <div>
            <Label>Project Name</Label>
            <Input
              value={employeeForm.project_name}
              onChange={(e) => setEmployeeForm({ ...employeeForm, project_name: e.target.value })}
            />
          </div>
          <div>
            <Label>Last Promotion</Label>
            <Input
              type="date"
              value={employeeForm.last_promotion}
              onChange={(e) => setEmployeeForm({ ...employeeForm, last_promotion: e.target.value })}
            />
          </div>
          <div>
            <Label>Active Date</Label>
            <Input
              value={employeeForm.active_date ? new Date(employeeForm.active_date).toLocaleString() : 'Not set'}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              data-testid="active-date-input"
            />
            <p className="text-xs text-gray-500 mt-1">Auto-updated when employee is activated</p>
          </div>
          <div>
            <Label>Inactive Date</Label>
            <Input
              value={employeeForm.inactive_date ? new Date(employeeForm.inactive_date).toLocaleString() : 'Not set'}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              data-testid="inactive-date-input"
            />
            <p className="text-xs text-gray-500 mt-1">Auto-updated when employee is deactivated</p>
          </div>
          <div className="col-span-2">
            <Label>Payslip Folder ID (Google Drive)</Label>
            <Input
              value={employeeForm.payslip_folder_id || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, payslip_folder_id: e.target.value })}
              placeholder="Google Drive Folder ID containing payslips"
              data-testid="payslip-folder-id-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the Google Drive folder ID that contains this employee's payslip PDFs. 
              The folder should be shared with the organization.
            </p>
          </div>
        </div>
      </TabsContent>

      {/* Salary Structure Tab */}
      <TabsContent value="salary" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Currency</Label>
            <Select
              value={employeeForm.salary_currency || ''}
              onValueChange={(value) => setEmployeeForm({ ...employeeForm, salary_currency: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR (₹)</SelectItem>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
                <SelectItem value="GBP">GBP (£)</SelectItem>
                <SelectItem value="AED">AED (د.إ)</SelectItem>
                <SelectItem value="SGD">SGD (S$)</SelectItem>
                <SelectItem value="AUD">AUD (A$)</SelectItem>
                <SelectItem value="CAD">CAD (C$)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fixed Monthly Salary</Label>
            <Input
              type="text"
              value={employeeForm.fixed_monthly_salary || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, fixed_monthly_salary: e.target.value })}
              placeholder="e.g., 50000 or NA"
            />
          </div>
          <div>
            <Label>Internet & Phone Allowance</Label>
            <Input
              type="text"
              value={employeeForm.internet_phone_allowance || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, internet_phone_allowance: e.target.value })}
              placeholder="e.g., 2000 or NA"
            />
          </div>
          <div>
            <Label>Monthly Performance Bonus</Label>
            <Input
              type="text"
              value={employeeForm.monthly_performance_bonus || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, monthly_performance_bonus: e.target.value })}
              placeholder="e.g., 5000 or NA"
            />
          </div>
          <div>
            <Label>BYOD Allowance</Label>
            <Input
              type="text"
              value={employeeForm.byod_allowance || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, byod_allowance: e.target.value })}
              placeholder="e.g., 3000 or NA"
            />
          </div>
          <div>
            <Label>Half Yearly Retention Bonus</Label>
            <Input
              type="text"
              value={employeeForm.half_yearly_retention_bonus || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, half_yearly_retention_bonus: e.target.value })}
              placeholder="e.g., 25000 or NA"
            />
          </div>
          <div>
            <Label>Managerial Allowance</Label>
            <Input
              type="text"
              value={employeeForm.managerial_allowance || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, managerial_allowance: e.target.value })}
              placeholder="e.g., 10000 or NA"
            />
          </div>
          <div>
            <Label>Provident Fund</Label>
            <Input
              type="text"
              value={employeeForm.provident_fund || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, provident_fund: e.target.value })}
              placeholder="e.g., 1800 or NA"
            />
          </div>
          <div>
            <Label>Medical Allowance</Label>
            <Input
              type="text"
              value={employeeForm.medical_allowance || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, medical_allowance: e.target.value })}
              placeholder="e.g., 1250 or NA"
            />
          </div>
          <div>
            <Label>Gratuity</Label>
            <Input
              type="text"
              value={employeeForm.gratuity || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, gratuity: e.target.value })}
              placeholder="e.g., 2000 or NA"
            />
          </div>
          <div>
            <Label>HRA (House Rent Allowance)</Label>
            <Input
              type="text"
              value={employeeForm.hra || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, hra: e.target.value })}
              placeholder="e.g., 20000 or NA"
            />
          </div>
          <div>
            <Label>CTC (Yearly)</Label>
            <Input
              type="text"
              value={employeeForm.ctc_yearly || ''}
              onChange={(e) => setEmployeeForm({ ...employeeForm, ctc_yearly: e.target.value })}
              placeholder="e.g., 1200000 or NA"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Note: Enter numeric values for salary components. Use "NA" for components not applicable to this employee.
        </p>
      </TabsContent>
    </Tabs>
  );
};

export default EmployeeFormFields;
